// Sitewide, dependency-vrije interactielaag (mobiel menu, FAQ-accordeon,
// cookie-consent, analytics-events). Bewust in vanilla TS i.p.v. React:
// dit is lichte DOM-scripting, geen component-state nodig — houdt de
// JS-bundle voor elke pagina minimaal.

import { trackEvent, hasAnalyticsConsent, initAnalyticsGate } from "../analytics.js";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

/** Stuurt Consent Mode v2-signalen door naar Google (no-op als GTM niet geconfigureerd is,
 * want dan bestaat window.gtag niet — zie render.ts). */
function updateConsentMode(granted: boolean) {
  const state = granted ? "granted" : "denied";
  window.gtag?.("consent", "update", {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  });
}

function initMobileMenu() {
  const toggle = document.querySelector<HTMLButtonElement>("[data-mobile-menu-toggle]");
  const menu = document.querySelector<HTMLElement>("[data-mobile-menu]");
  const closeBtn = document.querySelector<HTMLButtonElement>("[data-mobile-menu-close]");
  if (!toggle || !menu || !closeBtn) return;

  let lastFocused: HTMLElement | null = null;

  function open() {
    lastFocused = document.activeElement as HTMLElement;
    menu!.setAttribute("data-open", "true");
    toggle!.setAttribute("aria-expanded", "true");
    document.body.style.overflow = "hidden";
    closeBtn!.focus();
    document.addEventListener("keydown", onKeydown);
  }
  function close() {
    menu!.setAttribute("data-open", "false");
    toggle!.setAttribute("aria-expanded", "false");
    document.body.style.overflow = "";
    document.removeEventListener("keydown", onKeydown);
    lastFocused?.focus();
  }
  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      close();
      return;
    }
    if (e.key === "Tab") {
      const focusables = menu!.querySelectorAll<HTMLElement>('a, button, input, [tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  toggle.addEventListener("click", open);
  closeBtn.addEventListener("click", close);
  menu.addEventListener("click", (e) => {
    if (e.target === menu) close();
  });
  menu.querySelectorAll("a").forEach((a) => a.addEventListener("click", close));
}

function initFaqAccordion() {
  document.querySelectorAll<HTMLElement>("[data-faq-item]").forEach((item) => {
    const trigger = item.querySelector<HTMLButtonElement>("[data-faq-trigger]");
    trigger?.addEventListener("click", () => {
      const isOpen = item.getAttribute("data-open") === "true";
      item.setAttribute("data-open", isOpen ? "false" : "true");
      trigger.setAttribute("aria-expanded", String(!isOpen));
    });
  });
}

function initCookieConsent() {
  const banner = document.querySelector<HTMLElement>("[data-cookie-banner]");
  if (!banner) return;
  const STORAGE_KEY = "aag_cookie_consent";
  const stored = getCookie(STORAGE_KEY);

  if (!stored) {
    banner.setAttribute("data-visible", "true");
  } else {
    initAnalyticsGate(stored === "all");
  }

  banner.querySelector("[data-cookie-accept-all]")?.addEventListener("click", () => {
    setCookie(STORAGE_KEY, "all");
    banner.setAttribute("data-visible", "false");
    initAnalyticsGate(true);
    updateConsentMode(true);
  });
  banner.querySelector("[data-cookie-reject]")?.addEventListener("click", () => {
    setCookie(STORAGE_KEY, "functional");
    banner.setAttribute("data-visible", "false");
    initAnalyticsGate(false);
    updateConsentMode(false);
  });
  banner.querySelector("[data-cookie-customize]")?.addEventListener("click", () => {
    const settings = banner.querySelector<HTMLElement>("[data-cookie-settings]");
    if (settings) settings.hidden = !settings.hidden;
  });
}

function setCookie(name: string, value: string, days = 180) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
}
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function initPrintButtons() {
  document.querySelectorAll<HTMLButtonElement>("[data-print-button]").forEach((btn) => {
    btn.addEventListener("click", () => window.print());
  });
}

function initAnalyticsClicks() {
  document.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement)?.closest<HTMLElement>("[data-event]");
    if (!el) return;
    const eventName = el.getAttribute("data-event");
    if (eventName) trackEvent(eventName);
  });
}

/** Vuurt events die bij page-load al moeten gebeuren (bv. booking_confirmed op
 * /boeking-bevestigd), i.p.v. vlak vóór een window.location.href-navigatie op de vorige
 * pagina — die navigatie gooit de dataLayer daar meteen weg voordat GTM 'm kan verwerken.
 * Markering via data-attributen (zelfde patroon als [data-event] hierboven), zodat elke
 * server-gerenderde pagina dit gewoon kan declareren zonder eigen client-JS. */
function initLoadEvents() {
  document.querySelectorAll<HTMLElement>("[data-track-load-event]").forEach((el) => {
    const eventName = el.getAttribute("data-track-load-event");
    if (!eventName) return;

    let payload: Record<string, unknown> | undefined;
    const raw = el.getAttribute("data-track-payload");
    if (raw) {
      try {
        payload = JSON.parse(raw);
      } catch {
        payload = undefined;
      }
    }

    // Dedupe op een refresh van dezelfde pagina (bv. F5 op de bevestigingspagina), zodat
    // een conversie niet dubbel geteld wordt. Bij ontbrekend/onbruikbaar sessionStorage
    // (privénavigatie e.d.) liever één keer te veel tellen dan events kwijtraken.
    const dedupeId = payload && typeof payload.bookingNumber === "string" ? payload.bookingNumber : null;
    const dedupeKey = dedupeId ? `aag_tracked_${eventName}_${dedupeId}` : null;
    try {
      if (dedupeKey && sessionStorage.getItem(dedupeKey)) return;
    } catch {
      // negeren
    }

    trackEvent(eventName, payload);

    try {
      if (dedupeKey) sessionStorage.setItem(dedupeKey, "1");
    } catch {
      // negeren
    }
  });
}

function initReducedMotionAwareReveal() {
  // Placeholder-hook: bewust GEEN scroll-reveal-animaties toegevoegd
  // (spec vraagt terughoudend animatiegebruik + respect voor prefers-reduced-motion).
}

document.addEventListener("DOMContentLoaded", () => {
  initMobileMenu();
  initFaqAccordion();
  initCookieConsent();
  initPrintButtons();
  initAnalyticsClicks();
  initLoadEvents();
  initReducedMotionAwareReveal();
});
